import { useMutation, useQueryClient } from 'react-query';

import { FormActions, NotificationPosition } from 'coreOld/enums';
import { useHandleErrors, useInfiniteQuery } from 'coreOld/hooks';
import { actionLoadingMessage, actionMessage } from 'coreOld/utils';
import { userRanksService } from 'system/services';
import { IAccessRank } from 'system/types';

export const useGetUsersRanks = (filters?) =>
  useInfiniteQuery<IAccessRank>(userRanksService.getAllUsersRanks, 'systemUserRanks', filters);

export const useUserRanksActions = () => ({
  ...useSetUserRankResource(),
  ...useAddUserRanksBulk(),
  ...useRemoveUserRanksBulk(),
});

const useAddUserRanksBulk = () => {
  const queryClient = useQueryClient();

  const { errors } = useHandleErrors(
    actionMessage('User Ranks'),
    actionLoadingMessage('user ranks'),
    NotificationPosition.MODAL,
    ['systemUsers'],
    FormActions.ADD
  );

  const { mutateAsync } = useMutation((data) => userRanksService.addUserRanksBulk(data), {
    onSuccess: (data) => {
      queryClient.invalidateQueries(['systemUsers', data?.data?.internalReference]);
    },
    ...errors,
  });

  const addUserRanksBulk = (data) => mutateAsync(data);

  return { addUserRanksBulk };
};

const useRemoveUserRanksBulk = () => {
  const queryClient = useQueryClient();

  const { errors } = useHandleErrors(
    actionMessage('User Ranks'),
    actionLoadingMessage('user ranks'),
    NotificationPosition.MODAL,
    ['systemUsers'],
    FormActions.REMOVE
  );

  const { mutateAsync } = useMutation((data) => userRanksService.removeUserRanksBulk(data), {
    onSuccess: (data) => {
      queryClient.invalidateQueries(['systemUsers', data?.data?.internalReference]);
    },
    ...errors,
  });

  const removeUserRanksBulk = (data) => mutateAsync(data);

  return { removeUserRanksBulk };
};

const useSetUserRankResource = () => {
  const { errors } = useHandleErrors(
    actionMessage('User Rank Access'),
    actionLoadingMessage('user rank access'),
    NotificationPosition.TOP,
    [],
    FormActions.UPDATE
  );

  const { mutateAsync, isLoading } = useMutation(
    (data) => userRanksService.setUserRankResource(data),
    { ...errors }
  );

  const setUserRankResource = (data) => mutateAsync(data);

  return { setUserRankResource, setUserRankResourceIsLoading: isLoading };
};
